import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import type {ColumnDef} from "@tanstack/react-table";
import type {ProfileListItem} from "crqlar-react-common/dist/models/profile/profileListItem.model";
import {convertLocalDateToDate} from "crqlar-react-common/dist/utils/date.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {DateElement} from "../common/uiElements/DateElement";
import {CountryElement} from "../common/uiElements/CountryElement";
import {LanguagesElement} from "../common/uiElements/LanguagesElement";
import {NationalityElement} from "../common/uiElements/NationalityElement";
import {AccommodationElement} from "../common/uiElements/ResourceElement";
import {SimpleChipElement} from "../common/uiElements/SimpleChipElement";
import {StayStatusElement} from "../common/uiElements/StayStatusElement";

/**
 * The profile-list columns, shared by every grid that shows `ProfileListItem` rows: the CRM guest
 * list and the audience members list. One definition means one status cell, one print path and one
 * column chooser, whichever screen a guest appears on. Contact columns drop out for read-only users.
 */

const textList = (values: Array<string | undefined>) => (
    values.filter((value): value is string => Boolean(value))
);

const localizedInterestValues = (profile: ProfileListItem, locale: string) => (
    textList(profile.interests.map(interest => interest.localizations?.[locale] ?? interest.value))
);

const booleanLabel = (
    value: boolean | undefined,
    translate: (value: string) => string,
) => value === undefined ? null : translate(value ? "Yes" : "No");

const ageForBirthDay = (birthDay?: string) => {
    const birthDate = convertLocalDateToDate(birthDay);
    if (!birthDate) return undefined;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const birthdayThisYear = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());

    if (today < birthdayThisYear) age -= 1;
    return age >= 0 ? age : undefined;
};

function ProfileChipList({labels}: {labels: string[]}) {
    if (!labels.length) return null;

    return (
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5}}>
            {labels.map(label => (
                <Chip key={label} label={label} size="small" variant="outlined"/>
            ))}
        </Box>
    );
}

export const useProfileListColumns = (): ColumnDef<ProfileListItem>[] => {
    const {t, i18n} = useTranslation();
    const readOnlyUser = useReadOnly();
    const locale = i18n.resolvedLanguage || i18n.language;

    return useMemo<ColumnDef<ProfileListItem>[]>(() => {
        const profileColumns: ColumnDef<ProfileListItem>[] = [
            {
                id: "name",
                header: t("Name"),
                accessorFn: profile => formatProfileListName(profile),
                cell: ({getValue}) => <Box>{getValue<string>()}</Box>,
                size: 240,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    grow: true,
                    printable: true,
                    printDefault: true,
                },
            },
            {
                id: "lastNameDetails",
                header: t("Last name"),
                accessorFn: profile => profile.lastName,
                cell: ({row}) => <Box>{row.original.lastName ?? null}</Box>,
                size: 180,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "lastName",
                    printable: true,
                },
            },
            {
                id: "firstNameDetails",
                header: t("First name"),
                accessorFn: profile => profile.firstName,
                cell: ({row}) => <Box>{row.original.firstName ?? null}</Box>,
                size: 180,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "firstName",
                    printable: true,
                },
            },
            {
                id: "nationality",
                header: t("Nationality"),
                accessorFn: profile => profile.nationality,
                cell: ({row}) => <NationalityElement nationality={row.original.nationality}/>,
                size: 130,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <NationalityElement nationality={row.original.nationality} asPrint/>,
                },
            },
            {
                id: "countryCode",
                header: t("Country"),
                accessorFn: profile => profile.countryCode,
                cell: ({row}) => <CountryElement countryCode={row.original.countryCode}/>,
                size: 130,
                meta: {
                    defaultActive: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <CountryElement countryCode={row.original.countryCode} asPrint/>,
                },
            },
            {
                id: "gender",
                header: t("Gender"),
                accessorFn: profile => profile.gender,
                cell: ({row}) => (
                    <Box>{row.original.gender ? t(`Gender type ${row.original.gender}`) : null}</Box>
                ),
                size: 150,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => row.original.gender ? t(`Gender type ${row.original.gender}`) : null,
                },
            },
            {
                id: "birthDay",
                header: t("Birthday"),
                accessorFn: profile => profile.birthDay,
                cell: ({row}) => <DateElement date={row.original.birthDay} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.birthDay} dateOnly/>,
                },
            },
            {
                id: "status",
                header: t("Stay status"),
                accessorFn: profile => profile.stayStatus,
                cell: ({row}) => (
                    <StayStatusElement
                        stayStatus={row.original.stayStatus}
                        startDate={row.original.selectedStay?.startDay}
                        endDate={row.original.selectedStay?.endDay}
                    />
                ),
                size: 170,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: "activeStays.status",
                    printable: true,
                    printDefault: true,
                    printCell: row => <StayStatusElement stayStatus={row.original.stayStatus} asPrint/>,
                },
            },
            {
                id: "room",
                header: t("Room"),
                accessorFn: profile => profile.selectedStay?.roomId,
                cell: ({row}) => row.original.selectedStay?.roomId
                    ? (
                        <Box data-no-row-click>
                            <AccommodationElement name={row.original.selectedStay.roomId}/>
                        </Box>
                    )
                    : null,
                size: 130,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: "activeStays.roomId",
                    printable: true,
                    printDefault: true,
                    printCell: row => row.original.selectedStay?.roomId
                        ? <AccommodationElement name={row.original.selectedStay.roomId} asPrint/>
                        : null,
                },
            },
            {
                id: "interests",
                header: t("Interests"),
                accessorFn: profile => localizedInterestValues(profile, locale),
                cell: ({row}) => (
                    <Box data-no-row-click sx={{py: 0.5}}>
                        <SimpleChipElement values={localizedInterestValues(row.original, locale)}/>
                    </Box>
                ),
                size: 250,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: "smartData.interests.interest",
                    printable: true,
                    printDefault: true,
                    printCell: row => (
                        <SimpleChipElement values={localizedInterestValues(row.original, locale)} asPrint/>
                    ),
                },
            },
            {
                id: "smartCategories",
                header: t("Smart Categories"),
                accessorFn: profile => textList(profile.smartCategories.map(category => category.text)),
                cell: ({row}) => (
                    <ProfileChipList
                        labels={textList(row.original.smartCategories.map(category => category.text))}
                    />
                ),
                size: 230,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: "smartData.categories.text",
                    printable: true,
                    printDefault: true,
                    printCell: row => textList(
                        row.original.smartCategories.map(category => category.text),
                    ).join(", "),
                },
            },
            {
                id: "arrival",
                header: t("Arrival"),
                accessorFn: profile => profile.selectedStay?.startDay,
                cell: ({row}) => <DateElement date={row.original.selectedStay?.startDay} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: "activeStays.startDay",
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.selectedStay?.startDay} dateOnly/>,
                },
            },
            {
                id: "departure",
                header: t("Departure"),
                accessorFn: profile => profile.selectedStay?.endDay,
                cell: ({row}) => <DateElement date={row.original.selectedStay?.endDay} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: "activeStays.endDay",
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.selectedStay?.endDay} dateOnly/>,
                },
            },
            {
                id: "age",
                header: t("Age"),
                accessorFn: profile => ageForBirthDay(profile.birthDay),
                cell: ({getValue}) => <Box>{getValue<number | undefined>() ?? null}</Box>,
                size: 100,
                meta: {
                    defaultActive: false,
                    printable: true,
                    printCell: row => ageForBirthDay(row.original.birthDay) ?? null,
                },
            },
            {
                id: "profileType",
                header: t("Profile type"),
                accessorFn: profile => profile.profileType,
                cell: ({row}) => (
                    <Box>
                        {row.original.profileType
                            ? t(`Profile type ${row.original.profileType}`)
                            : null}
                    </Box>
                ),
                size: 180,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "profileTypeC",
                    printable: true,
                    printCell: row => row.original.profileType
                        ? t(`Profile type ${row.original.profileType}`)
                        : null,
                },
            },
            {
                id: "profileStatus",
                header: t("Profile status"),
                accessorFn: profile => profile.profileStatus,
                cell: ({row}) => (
                    <Box>
                        {row.original.profileStatus
                            ? t(`Profile status ${row.original.profileStatus}`)
                            : null}
                    </Box>
                ),
                size: 170,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "statusCodeC",
                    printable: true,
                    printCell: row => row.original.profileStatus
                        ? t(`Profile status ${row.original.profileStatus}`)
                        : null,
                },
            },
            {
                id: "recommendations",
                header: t("Recommendations"),
                accessorFn: profile => profile.hasRecommendations,
                cell: ({row}) => row.original.hasRecommendations
                    ? <Chip label={t("New recommendations!")} size="small" color="primary" variant="outlined"/>
                    : null,
                size: 210,
                meta: {
                    defaultActive: false,
                    printable: true,
                    printCell: row => row.original.hasRecommendations ? t("New recommendations!") : null,
                },
            },
            {
                id: "academicTitle",
                header: t("Academic title"),
                accessorFn: profile => profile.academicTitle,
                cell: ({row}) => <Box>{row.original.academicTitle ?? null}</Box>,
                size: 160,
                meta: {defaultActive: false, isServerSortable: true, printable: true},
            },
            {
                id: "languages",
                header: t("Languages"),
                accessorFn: profile => profile.languages,
                cell: ({row}) => <LanguagesElement languageCodes={row.original.languages}/>,
                size: 150,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "languageCode",
                    printable: true,
                    printCell: row => <LanguagesElement languageCodes={row.original.languages} asPrint/>,
                },
            },
            {
                id: "namePrefix",
                header: t("Name prefix"),
                accessorFn: profile => profile.namePrefix,
                cell: ({row}) => <Box>{row.original.namePrefix ?? null}</Box>,
                size: 150,
                meta: {defaultActive: false, isServerSortable: true, printable: true},
            },
            {
                id: "email",
                header: t("Email"),
                accessorFn: profile => profile.emails,
                cell: ({row}) => <Box>{row.original.emails.length ? row.original.emails.join(", ") : null}</Box>,
                size: 260,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "emails.email",
                    printable: true,
                    printCell: row => row.original.emails.length ? row.original.emails.join(", ") : null,
                },
            },
            {
                id: "primaryPhone",
                header: t("Phone"),
                accessorFn: profile => profile.primaryPhone,
                cell: ({row}) => <Box>{row.original.primaryPhone ?? null}</Box>,
                size: 190,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "phones.phoneNumberFull",
                    printable: true,
                },
            },
            {
                id: "companyName",
                header: t("Company"),
                accessorFn: profile => profile.companyName,
                cell: ({row}) => <Box>{row.original.companyName ?? null}</Box>,
                size: 210,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "company.companyName",
                    printable: true,
                },
            },
            {
                id: "totalStays",
                header: t("Total stays"),
                accessorFn: profile => profile.totalStays,
                cell: ({row}) => <Box>{row.original.totalStays ?? null}</Box>,
                size: 130,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "smartData.stays.totalStays",
                    printable: true,
                },
            },
            {
                id: "totalNights",
                header: t("Total nights"),
                accessorFn: profile => profile.totalNights,
                cell: ({row}) => <Box>{row.original.totalNights ?? null}</Box>,
                size: 130,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "smartData.stays.totalNights",
                    printable: true,
                },
            },
            {
                id: "lastStayDate",
                header: t("Last stay"),
                accessorFn: profile => profile.lastStayDate,
                cell: ({row}) => <DateElement date={row.original.lastStayDate} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "smartData.stays.lastStayDate",
                    printable: true,
                    printCell: row => <DateElement date={row.original.lastStayDate} dateOnly/>,
                },
            },
            {
                id: "nextStayDate",
                header: t("Next stay"),
                accessorFn: profile => profile.nextStayDate,
                cell: ({row}) => <DateElement date={row.original.nextStayDate} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: "smartData.stays.nextStayDate",
                    printable: true,
                    printCell: row => <DateElement date={row.original.nextStayDate} dateOnly/>,
                },
            },
            {
                id: "receiveNewsletters",
                header: t("Receives newsletters"),
                accessorFn: profile => profile.receiveNewsletters,
                cell: ({row}) => <Box>{booleanLabel(row.original.receiveNewsletters, t)}</Box>,
                size: 190,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => booleanLabel(row.original.receiveNewsletters, t),
                },
            },
            {
                id: "primaryProvider",
                header: t("Source"),
                accessorFn: profile => profile.primaryProvider,
                cell: ({row}) => <Box>{row.original.primaryProvider ?? null}</Box>,
                size: 170,
                meta: {defaultActive: false, isServerSortable: true, printable: true},
            },
            {
                id: "insertDate",
                header: t("Created"),
                accessorFn: profile => profile.insertDate,
                cell: ({row}) => <DateElement date={row.original.insertDate}/>,
                size: 190,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => <DateElement date={row.original.insertDate}/>,
                },
            },
            {
                id: "id",
                header: t("ID"),
                accessorFn: profile => profile.id,
                cell: ({row}) => <Box>{row.original.id}</Box>,
                size: 260,
                meta: {defaultActive: false, isServerSortable: true},
            },
            {
                id: "updateDate",
                header: t("Last updated"),
                accessorFn: profile => profile.updateDate,
                cell: ({row}) => <DateElement date={row.original.updateDate}/>,
                size: 190,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.updateDate}/>,
                },
            },
        ];

        return readOnlyUser
            ? profileColumns.filter(column => column.id !== "email" && column.id !== "primaryPhone")
            : profileColumns;
    }, [locale, readOnlyUser, t]);
};
